import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import { AnyPromotionInfo, SerializedException, exceptionOf } from '../models';
import * as PromotionService from '../services/promotion';

export const PROMOTION_FEATURE_KEY = 'promotion';

const promotionAdapter = createEntityAdapter<AnyPromotionInfo>();

export interface PromotionState extends EntityState<AnyPromotionInfo> {
  activePromotion: AnyPromotionInfo | null;
  loading: boolean;
  errors: SerializedException[];
}

export const createInitialState = (): PromotionState => promotionAdapter.getInitialState({
  activePromotion: null,
  loading: false,
  errors: [],
});

// Async thunks
export const doFetchActivePromotion = createAsyncThunk(
  'promotion/fetchActivePromotion',
  async (_, { rejectWithValue }) => {
    try {
      const promotion = await PromotionService.getActivePromotion();
      return promotion;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const promotionSlice = createSlice({
  name: PROMOTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    clearActivePromotion: state => {
      state.activePromotion = null;
    },
    clearErrors: state => {
      state.errors = [];
    },
  },
  extraReducers: builder => {
    builder
      // Fetch active promotion
      .addCase(doFetchActivePromotion.pending, state => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(
        doFetchActivePromotion.fulfilled,
        (state, action: PayloadAction<AnyPromotionInfo | null>) => {
          state.loading = false;
          state.activePromotion = action.payload;
        },
      )
      .addCase(doFetchActivePromotion.rejected, (state, action) => {
        state.loading = false;
        state.errors = [action.payload as SerializedException];
      });
  },
});

// Selectors
export const selectPromotionState = (state: AppState) => state[PROMOTION_FEATURE_KEY];

export const selectActivePromotion = (state: AppState) => (
  selectPromotionState(state).activePromotion
);

export const selectPromotionLoading = (state: AppState) => (
  selectPromotionState(state).loading
);

export const selectPromotionErrors = (state: AppState) => (
  selectPromotionState(state).errors
);

export const selectCampaignCode = (state: AppState) => (
  selectPromotionState(state).activePromotion?.campaignCode
);

// Actions
export const { clearActivePromotion, clearErrors } = promotionSlice.actions;

// Reducer
export default promotionSlice.reducer;

// Export namespace for consistent usage pattern
export const fromPromotion = {
  doFetchActivePromotion,
  selectActivePromotion,
  selectPromotionLoading,
  selectPromotionErrors,
  selectCampaignCode,
  clearActivePromotion,
  clearErrors,
};
